import { Column, Line } from '@ant-design/charts'
import { Alert, Card, Col, Row, Statistic } from 'antd'

import { ChartSpin, useChartTheme } from '@/components/charts/chartTheme'
import { formatUsdCents } from '@/utils/money'

import type { AdminMetrics } from '../hooks/useAdminMetrics'

function CreditedChart({ data }: { data: AdminMetrics['creditedUsd'] }) {
  const chart = useChartTheme()
  return (
    <Column
      data={data}
      xField="date"
      yField="value"
      height={250}
      axis={{
        x: { ...chart.axis.x, labelAutoHide: true },
        y: { ...chart.axis.y, labelFormatter: (v: number) => `$${v}` },
      }}
      tooltip={{
        items: [{ channel: 'y', valueFormatter: (v: number) => `$${v}` }],
      }}
      legend={false}
      style={{ fill: '#52c41a', maxWidth: 12 }}
    />
  )
}

function NewUsersChart({ data }: { data: AdminMetrics['newUsers'] }) {
  const chart = useChartTheme()
  return (
    <Line
      data={data}
      xField="date"
      yField="value"
      height={250}
      axis={{ x: { ...chart.axis.x, labelAutoHide: true }, y: chart.axis.y }}
      legend={false}
      style={{ lineWidth: 2 }}
      color="#13c2c2"
      point={{ size: 3, shape: 'circle' }}
    />
  )
}

export function MetricsCharts({
  metrics,
  loading,
  error,
}: {
  metrics?: AdminMetrics
  loading: boolean
  error: boolean
}) {
  if (error && !metrics) {
    return (
      <Alert
        type="error"
        message="Failed to load dashboard metrics"
        showIcon
        style={{ marginTop: 16 }}
      />
    )
  }

  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col xs={24} lg={14}>
        <Card
          title="Credited to Balances (30 days)"
          extra={
            metrics ? (
              <Statistic
                value={formatUsdCents(metrics.creditedTotalCents)}
                valueStyle={{ fontSize: 16 }}
              />
            ) : null
          }
        >
          {loading || !metrics ? (
            <ChartSpin />
          ) : (
            <CreditedChart data={metrics.creditedUsd} />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={10}>
        <Card
          title="New Users (30 days)"
          extra={
            metrics ? (
              <Statistic
                value={metrics.newUsersTotal}
                valueStyle={{ fontSize: 16 }}
              />
            ) : null
          }
        >
          {loading || !metrics ? (
            <ChartSpin />
          ) : (
            <NewUsersChart data={metrics.newUsers} />
          )}
        </Card>
      </Col>
    </Row>
  )
}
